import { useEffect, useState } from "react";
import PhoneInput, {
  Country,
  isPossiblePhoneNumber,
  getCountries,
  DefaultInputComponentProps,
} from "react-phone-number-input";
import "react-phone-number-input/style.css";
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  Path,
} from "react-hook-form";
import {
  Color,
  Field,
  FormFieldStatus,
  Hint,
  Input,
  Label,
  useTheme,
} from "@gocardless/flux-react";
import { Trans } from "@lingui/macro";

import { getInputErrorStatus } from "src/utils/inputValidation";
import { FieldLevelError } from "src/common/api";
import {
  CountryCodeNotSupported,
  CountryCodeSupported,
  CountryCodes,
} from "src/common/country";

export interface PhoneNumberProps<T extends FieldValues> {
  defaultCountryGeo?: string;
  name?: string;
  control?: Control<T>;
  required?: boolean;
  error?: FieldError;
  apiError?: FieldLevelError;
  className?: string;
  label?: string | JSX.Element;
  htmlFor?: string;
  supportedGeosOnly?: boolean;
}

const PhoneNumberInput = <T extends FieldValues>({
  defaultCountryGeo,
  name,
  control,
  required,
  error,
  apiError,
  className,
  label,
  supportedGeosOnly,
  htmlFor,
}: PhoneNumberProps<T>) => {
  const [numberValue, setNumberValue] = useState("");
  const [defaultCountry, setDefaultCountry] = useState("");

  const { theme } = useTheme();

  useEffect(() => {
    if (
      defaultCountryGeo &&
      defaultCountryGeo !== defaultCountry &&
      !numberValue
    ) {
      setDefaultCountry(defaultCountryGeo);
    }
    // TODO: Fix exhaustive dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCountryGeo]);

  const fieldName = name || "phone_number";
  const labelText = label || <Trans id="Phone number">Phone number</Trans>;
  const labelHtmlFor = htmlFor || "phone_number";

  const supportedCountryCodes = Object.values(CountryCodeSupported);
  const ExtraSupportedCountriesForPhone = [
    CountryCodes.GF,
    CountryCodes.GP,
    CountryCodes.IL,
    CountryCodes.LV,
    CountryCodes.RE,
    CountryCodes.YT,
  ];
  let countryList: DefaultInputComponentProps["countries"];

  if (supportedGeosOnly) {
    countryList = getCountries().filter(
      (code) =>
        supportedCountryCodes.includes(code as CountryCodeSupported) ||
        ExtraSupportedCountriesForPhone.includes(code as CountryCodes)
    );
  } else {
    countryList = getCountries().filter(
      (code) => !CountryCodeNotSupported.includes(code as CountryCodes)
    );
  }

  return (
    <Field data-testid={fieldName}>
      <Label htmlFor={labelHtmlFor}>{labelText}</Label>
      <Controller
        name={fieldName as Path<T>}
        control={control}
        rules={{
          required: required,
          validate: (value: string) =>
            value ? isPossiblePhoneNumber(value) : false,
        }}
        render={({ field: { onChange, ...fieldProps } }) => (
          <PhoneInput
            {...fieldProps}
            type="tel"
            name={fieldName}
            countries={countryList}
            onCountryChange={(country) => setDefaultCountry(country as string)}
            defaultCountry={defaultCountry ? (defaultCountry as Country) : "GB"}
            id="phone_number"
            inputComponent={Input}
            status={getInputErrorStatus(!!error)}
            required={required}
            international
            countryCallingCodeEditable={false}
            css={{
              position: "relative",
              marginTop: "8px",
              width: "100%",
              ".PhoneInputCountry": {
                position: "absolute",
                top: "50%",
                transform: "translateY(-50%)",
                left: "16px",
                "--PhoneInputCountrySelectArrow-borderWidth": "2px",
                ".PhoneInputCountryIcon--border": {
                  boxShadow: "initial",
                  borderRadius: "4px",
                  overflow: "hidden",
                },
                ".PhoneInputCountrySelectArrow": {
                  opacity: 1,
                  width: "0.5em",
                  height: "0.5em",
                  marginLeft: "0.5em",
                  marginBottom: "0.2em",
                  borderColor: theme.color(Color.Greystone_DarkMatter),
                },
                "~ div": {
                  paddingLeft: "48px",
                  width: "100%",
                },
              },
            }}
            className={className}
            onChange={(data) => {
              const formattedNumber = data || "";
              setNumberValue(formattedNumber);
              return onChange(formattedNumber);
            }}
          />
        )}
      />
      {error?.type === "validate" && (
        <Hint status={FormFieldStatus.Danger}>
          <Trans id="phone-number.field-validation-warning">
            Please enter a valid phone number
          </Trans>
        </Hint>
      )}
      {(error?.type === "required" || error?.types?.type === "required") && (
        <Hint status={FormFieldStatus.Danger}>
          <Trans id="phone-number.field-required-warning">
            Phone number is required
          </Trans>
        </Hint>
      )}
      {apiError && (
        <Hint status={FormFieldStatus.Danger}>{apiError.message}</Hint>
      )}
    </Field>
  );
};

export default PhoneNumberInput;
